import { useConfirmModal, useDropTarget } from '@affine/component';
import { useTrashDoc } from '@affine/core/components/hooks/affine/use-trash-doc';
import { MenuLinkItem } from '@affine/core/modules/app-sidebar/views';
import { DocsService } from '@affine/core/modules/doc';
import { GlobalContextService } from '@affine/core/modules/global-context';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useI18n } from '@affine/i18n';
import { DeleteIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import type { ReactElement } from 'react';

import type { NavigationPanelTreeSelectionItem } from '../../desktop/components/navigation-panel/tree';
import { getNavigationPanelDragSelectionFromDraggable } from '../../desktop/components/navigation-panel/tree/selection-dnd';
import { filterNavigationPanelTrashableSelectionItems } from '../../desktop/components/navigation-panel/tree/selection-trash-capability';
import { useNavigationPanelTrashActions } from '../../desktop/components/navigation-panel/tree/use-navigation-panel-trash-actions';

export const TrashButton = ({ postfix }: { postfix?: ReactElement }) => {
  const t = useI18n();
  const docsService = useService(DocsService);
  const { openConfirmModal } = useConfirmModal();
  const globalContextService = useService(GlobalContextService);
  const trashActive = useLiveData(globalContextService.globalContext.isTrash.$);
  const { trashNavigationPanelItems } = useNavigationPanelTrashActions();
  const trashDoc = useTrashDoc();

  const { dropTargetRef, draggedOver } = useDropTarget<AffineDNDData>(
    () => ({
      data: {
        at: 'app-sidebar:trash',
      },
      canDrop(data) {
        return (
          filterNavigationPanelTrashableSelectionItems(
            getNavigationPanelDragSelectionFromDraggable(data.source.data)
          ).length > 0
        );
      },
      onDrop(data) {
        const trashableItems = filterNavigationPanelTrashableSelectionItems(
          getNavigationPanelDragSelectionFromDraggable(data.source.data)
        );
        if (!trashableItems.length) {
          return;
        }
        const docItems = trashableItems.filter(
          (item): item is NavigationPanelTreeSelectionItem =>
            item.entity.type === 'doc'
        );
        if (
          trashableItems.length > 1 ||
          trashableItems.some(item => item.entity.type === 'folder')
        ) {
          void trashNavigationPanelItems(trashableItems);
          return;
        }
        if (docItems.length === 1) {
          const docId = docItems[0].entity.id;
          const docRecord = docsService.list.doc$(docId).value;
          if (docRecord) {
            openConfirmModal({
              title: t['com.streamlain.moveToTrash.confirmModal.title'](),
              description: t[
                'com.streamlain.moveToTrash.confirmModal.description'
              ]({
                title: docRecord.title$.value || t['Untitled'](),
              }),
              confirmText: t.Delete(),
              confirmButtonOptions: {
                variant: 'error',
              },
              onConfirm() {
                void trashDoc(docRecord);
              },
            });
          }
        }
      },
      allowExternal: true,
    }),
    [docsService.list, openConfirmModal, t, trashDoc, trashNavigationPanelItems]
  );

  return (
    <MenuLinkItem
      ref={dropTargetRef}
      icon={<DeleteIcon />}
      active={trashActive || draggedOver}
      postfix={postfix}
      postfixDisplay={postfix ? 'always' : undefined}
      to={'/trash'}
    >
      <span data-testid="trash-page">
        {t['com.streamlain.workspaceSubPath.trash']()}
      </span>
    </MenuLinkItem>
  );
};
